import { useState } from "react";
import { ArrowDown, ArrowUp, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { WIDGETS } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const CustomizeSheet = () => {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState(user.preferences.widgets);
  const [saving, setSaving] = useState(false);

  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setItems(next);
  };
  const toggle = (id) => setItems(items.map((w) => (w.id === id ? { ...w, visible: !w.visible } : w)));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me/preferences", { widgets: items });
      setUser((u) => ({ ...u, preferences: data }));
      toast.success("Tableau de bord mis à jour");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet onOpenChange={(o) => { if (o) setItems(user.preferences.widgets); }}>
      <SheetTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="dashboard-customize-button">
          <SlidersHorizontal className="mr-2 h-4 w-4" />Personnaliser
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto" data-testid="dashboard-customize-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">Mes widgets</SheetTitle>
          <SheetDescription>Choisissez et ordonnez vos priorités. Sur smartphone, les 4 premiers s'affichent d'abord.</SheetDescription>
        </SheetHeader>
        <ul className="mt-6 space-y-2">
          {items.map((w, i) => {
            const def = WIDGETS[w.id];
            return (
              <li key={w.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`customize-item-${w.id}`}>
                <def.icon className="h-4 w-4 text-sage" />
                <span className="flex-1 text-sm">{def.title}</span>
                <button className="rounded-md p-1 hover:bg-secondary disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Monter" data-testid={`customize-up-${w.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button className="rounded-md p-1 hover:bg-secondary disabled:opacity-30" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Descendre" data-testid={`customize-down-${w.id}`}><ArrowDown className="h-4 w-4" /></button>
                <Switch checked={w.visible} onCheckedChange={() => toggle(w.id)} data-testid={`customize-toggle-${w.id}`} />
              </li>
            );
          })}
        </ul>
        <Button className="mt-6 w-full rounded-full" onClick={save} disabled={saving} data-testid="customize-save-button">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </SheetContent>
    </Sheet>
  );
};
